import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, of, retry, throwError } from 'rxjs';
import {
  Outing,
  CreateOuting,
  UpdateOuting,
  OutingReportSummary,
  PublicOutingRsvp,
  OutingAttachment,
  OutingAttachmentKind,
  PublicOutingPhotos,
  OutingMarketingEmailPreview,
  OutingMarketingEmailResult
} from '../models/outing.model';
import { environment } from '../../environments/environment';

export interface OutingFilters {
  year?: number;
  quarter?: number;
  projectId?: string;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OutingService {
  private apiUrl = `${environment.apiUrl}/Outing`;

  constructor(private http: HttpClient) { }

  private toParams(filters: OutingFilters): HttpParams {
    let params = new HttpParams();
    if (filters.year != null) params = params.set('year', filters.year);
    if (filters.quarter != null) params = params.set('quarter', filters.quarter);
    if (filters.projectId) params = params.set('projectId', filters.projectId);
    if (filters.status) params = params.set('status', filters.status);
    return params;
  }

  getAll(filters: OutingFilters = {}): Observable<Outing[]> {
    return this.http.get<Outing[]>(this.apiUrl, { params: this.toParams(filters) }).pipe(
      retry(2),
      catchError(error => this.handleError(error, 'Failed to load outings'))
    );
  }

  getReport(filters: OutingFilters = {}): Observable<OutingReportSummary | null> {
    return this.http.get<OutingReportSummary>(`${this.apiUrl}/report`, { params: this.toParams(filters) }).pipe(
      catchError(() => of(null))
    );
  }

  create(dto: CreateOuting): Observable<Outing> {
    return this.http.post<Outing>(this.apiUrl, dto).pipe(
      catchError(error => this.handleError(error, 'Failed to create outing'))
    );
  }

  update(id: string, dto: UpdateOuting): Observable<Outing> {
    return this.http.put<Outing>(`${this.apiUrl}/${id}`, dto).pipe(
      catchError(error => this.handleError(error, 'Failed to update outing'))
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => this.handleError(error, 'Failed to delete outing'))
    );
  }

  // Facts for the "email Marketing" confirmation dialog — sends nothing.
  getMarketingEmailPreview(id: string): Observable<OutingMarketingEmailPreview> {
    return this.http.get<OutingMarketingEmailPreview>(`${this.apiUrl}/${id}/marketing-email/preview`).pipe(
      catchError(error => this.handleError(error, 'Failed to load email details'))
    );
  }

  // Re-sends the "outing done" recap with the photos-page link (Done outings only).
  resendMarketingEmail(id: string): Observable<OutingMarketingEmailResult> {
    return this.http.post<OutingMarketingEmailResult>(`${this.apiUrl}/${id}/marketing-email/resend`, {}).pipe(
      catchError(error => this.handleError(error, 'Failed to send the email'))
    );
  }

  exportToExcel(filters: OutingFilters = {}): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/export-excel`, {
      params: this.toParams(filters),
      responseType: 'blob'
    }).pipe(
      catchError(error => this.handleError(error, 'Failed to export to Excel'))
    );
  }

  // Participation detail export. going: true=confirmed, false=not-confirmed, undefined=all.
  // serviceGroupId filters by employee service group; goneBucket is '0' | '1-2' | '3+'.
  exportParticipation(
    filters: OutingFilters = {},
    going?: boolean,
    serviceGroupId?: string | null,
    goneBucket?: string | null
  ): Observable<Blob> {
    let params = this.toParams(filters);
    if (going != null) params = params.set('going', going);
    if (serviceGroupId) params = params.set('serviceGroupId', serviceGroupId);
    if (goneBucket) params = params.set('goneBucket', goneBucket);
    return this.http.get(`${this.apiUrl}/export-participation`, { params, responseType: 'blob' }).pipe(
      catchError(error => this.handleError(error, 'Failed to export participation'))
    );
  }

  // ---- Attachments (photos + bills) ----
  getAttachments(outingId: string): Observable<OutingAttachment[]> {
    return this.http.get<OutingAttachment[]>(`${this.apiUrl}/${outingId}/attachments`).pipe(
      catchError(error => this.handleError(error, 'Failed to load attachments'))
    );
  }

  uploadAttachment(outingId: string, kind: OutingAttachmentKind, file: File): Observable<OutingAttachment> {
    const form = new FormData();
    form.append('kind', kind === OutingAttachmentKind.Photo ? 'Photo' : 'Bill');
    form.append('file', file);
    return this.http.post<OutingAttachment>(`${this.apiUrl}/${outingId}/attachments`, form).pipe(
      catchError(error => this.handleError(error, 'Failed to upload file'))
    );
  }

  deleteAttachment(attachmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/attachments/${attachmentId}`).pipe(
      catchError(error => this.handleError(error, 'Failed to delete file'))
    );
  }

  // Editable-dropdown lookups (option = event name; location). Grow as users type new values.
  getOptions(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/options`).pipe(
      retry(1), catchError(() => of([] as string[]))
    );
  }

  getLocations(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/locations`).pipe(
      retry(1), catchError(() => of([] as string[]))
    );
  }

  // In-app: download one attachment as a blob (so we can save with the real filename).
  downloadAttachment(attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/attachments/${attachmentId}/download`, { responseType: 'blob' }).pipe(
      catchError(error => this.handleError(error, 'Failed to download file'))
    );
  }

  // ---- Public photos page (token-based, no auth) ----
  getPublicPhotos(token: string): Observable<PublicOutingPhotos> {
    return this.http.get<PublicOutingPhotos>(`${this.apiUrl}/photos/${token}`).pipe(
      catchError(error => this.handleError(error, 'Failed to load photos'))
    );
  }

  downloadPublicPhoto(token: string, attachmentId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/photos/${token}/download/${attachmentId}`, { responseType: 'blob' }).pipe(
      catchError(error => this.handleError(error, 'Failed to download photo'))
    );
  }

  downloadAllPhotos(token: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/photos/${token}/download-all`, { responseType: 'blob' }).pipe(
      catchError(error => this.handleError(error, 'Failed to download photos'))
    );
  }

  // ---- Public RSVP (token-based, no auth) ----
  getRsvp(token: string): Observable<PublicOutingRsvp> {
    return this.http.get<PublicOutingRsvp>(`${this.apiUrl}/rsvp/${token}`).pipe(
      catchError(error => this.handleError(error, 'This invitation link is invalid or has expired'))
    );
  }

  confirmRsvp(token: string): Observable<PublicOutingRsvp> {
    return this.http.post<PublicOutingRsvp>(`${this.apiUrl}/rsvp/${token}/confirm`, {}).pipe(
      catchError(error => this.handleError(error, 'Could not confirm your attendance'))
    );
  }

  private handleError(error: any, message: string): Observable<never> {
    const serverMessage = error?.error?.details || error?.error?.message;
    console.error(`${message}:`, error);
    return throwError(() => new Error(serverMessage || `${message}. Please try again.`));
  }
}
